import { create } from 'zustand'
import { http } from '@/api/http'

export interface AdminProfile {
  id: string
  email: string
  name?: string
  roles: string[]
  permissions: string[]
  totp_enabled: boolean
}

interface SessionResp {
  session_id?: string
  profile?: AdminProfile
}

interface AuthState {
  profile: AdminProfile | null
  csrf: string
  // 部署区域与其推导币种，用于金额字段的取值与展示单位
  // （.specs/domain/commerce-pricing.md R3）
  region: string
  currency: string
  login: (email: string, password: string, totp?: string) => Promise<void>
  logout: () => Promise<void>
  fetchMe: () => Promise<void>
  fetchCsrf: () => Promise<void>
  fetchSettings: () => Promise<void>
}

export const useAuthStore = create<AuthState>((set) => ({
  profile: null,
  csrf: '',
  region: '',
  currency: 'CNY',
  async login(email, password, totp) {
    const res = (await http.post('/auth/login', { email, password, totp })) as SessionResp
    // 管理端登录响应不含 profile：立即用会话取回，避免路由守卫在 profile 为
    // null 时再次探测 /me 失败而把用户静默弹回登录页。
    const profile = res?.profile ?? ((await http.get('/me')) as AdminProfile)
    set({ profile })
    try {
      const s = (await http.get('/settings')) as { region?: string; currency?: string }
      set({ region: s.region ?? '', currency: s.currency || 'CNY' })
    } catch {
      /* keep the CNY default */
    }
    // CSRF 仅用于后续写操作，取不到不应阻断登录
    try {
      const r = (await http.get('/auth/csrf')) as { csrf: string }
      set({ csrf: r.csrf })
    } catch {
      /* keep empty csrf */
    }
  },
  async logout() {
    try {
      await http.post('/auth/logout', {})
    } catch {
      /* session already gone */
    }
    set({ profile: null, csrf: '', region: '', currency: 'CNY' })
  },
  async fetchMe() {
    const profile = (await http.get('/me')) as AdminProfile
    set({ profile })
    try {
      const s = (await http.get('/settings')) as { region?: string; currency?: string }
      set({ region: s.region ?? '', currency: s.currency || 'CNY' })
    } catch {
      /* keep the CNY default */
    }
  },
  async fetchSettings() {
    const s = (await http.get('/settings')) as { region?: string; currency?: string }
    set({ region: s.region ?? '', currency: s.currency || 'CNY' })
  },
  async fetchCsrf() {
    const r = (await http.get('/auth/csrf')) as { csrf: string }
    set({ csrf: r.csrf })
  },
}))
